import React, { type ComponentProps, useCallback, useEffect, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, type ViewInstance } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import Animated, { Easing, useAnimatedStyle, withTiming } from 'react-native-reanimated';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';

import { useAppState, useHovered } from '../../../hooks';
import { NoticeToastStackItem } from './components';
import { useFocusedWithin } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const NoticeToastStack = (props: Props) => {
  const {
    toasts,
    placement,
    topGap,
    bottomGap = 0,
    isLiquidGlassEnabled = false,
    onDismiss,
  } = props;

  const appState = useAppState();

  // ─── Refs ────────────────────────────────────────────────────────────

  const deckRef = useRef<ViewInstance>(null);

  const isHovered = useHovered(deckRef);
  const isFocusedWithin = useFocusedWithin(deckRef);

  // ─── State ───────────────────────────────────────────────────────────

  const [toastHeightsById, setToastHeightsById] = useState<ToastHeightsById>({});
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPressing, setIsPressing] = useState(false);
  const [isSwiping, setIsSwiping] = useState(false);

  // ─── Derived ─────────────────────────────────────────────────────────

  const visibleToasts = toasts.slice(0, MAX_VISIBLE_TOASTS);

  // Drawn back to front, not with zIndex: zIndex changes re-insert native views and break glass.
  const backToFrontIndexes = visibleToasts.map(toBackToFrontIndex);
  const hasMultipleToasts = visibleToasts.length > 1;
  const isDeckExpanded = hasMultipleToasts && (isExpanded || isHovered || isFocusedWithin);
  const isAppActive = appState === 'active';
  const isTouching = isPressing || isSwiping;
  const isPaused = isDeckExpanded || isHovered || isFocusedWithin || isTouching || !isAppActive;

  const toastHeights = visibleToasts.map((toast) => toastHeightsById[toast.id] ?? 0);
  const [frontToastHeight = 0] = toastHeights;
  const collapsedOffsets = toastHeights.map(toCollapsedOffset);
  const expandedOffsets = toastHeights.map(toExpandedOffset);
  const collapsedScales = toastHeights.map(toCollapsedScale);

  const collapsedDeckHeight = frontToastHeight + (visibleToasts.length - 1) * TOAST_PEEK;
  const expandedDeckHeight = sumHeights(toastHeights) + (visibleToasts.length - 1) * TOAST_GAP;
  const deckHeight = isDeckExpanded ? expandedDeckHeight : collapsedDeckHeight;
  const deckBottomGap = placement === 'bottom' ? bottomGap : 0;

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleToastLayout = useCallback((id: string, height: number) => {
    setToastHeightsById((currentHeights) => ({ ...currentHeights, [id]: height }));
  }, []);

  const toggleExpanded = useCallback(() => {
    if (Platform.OS === 'web' || !hasMultipleToasts) {
      return;
    }

    setIsExpanded((currentIsExpanded) => !currentIsExpanded);
  }, [hasMultipleToasts]);

  const handlePressIn = useCallback(() => {
    setIsPressing(true);
  }, []);

  const handlePressOut = useCallback(() => {
    setIsPressing(false);
  }, []);

  const handleSwipeStart = useCallback(() => {
    setIsSwiping(true);
  }, []);

  const handleSwipeEnd = useCallback(() => {
    setIsSwiping(false);
  }, []);

  const collapseSingleToast = useCallback(() => {
    if (hasMultipleToasts) {
      return;
    }

    setIsExpanded(false);
  }, [hasMultipleToasts]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(collapseSingleToast, [collapseSingleToast]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const topGapStyle = useStyle(() => {
    if (placement !== 'top') {
      return;
    }

    return { paddingTop: topGap };
  }, [placement, topGap]);

  // Lifts the whole layer, not the deck, so toasts stay inside the gesture root's touch area.
  const bottomGapStyle = useAnimatedStyle(
    () => ({ transform: [{ translateY: withTiming(-deckBottomGap, LAYOUT_TIMING) }] }),
    [deckBottomGap],
  );

  const deckHeightStyle = useAnimatedStyle(
    () => ({ height: withTiming(deckHeight, LAYOUT_TIMING) }),
    [deckHeight],
  );

  const offsetContainerStyles = [
    placement === 'bottom' ? styles.offsetContainerBottom : undefined,
    bottomGapStyle,
  ];
  const containerStyles = [styles.container, topGapStyle];
  const deckStyles = [styles.deck, deckHeightStyle];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Animated.View style={offsetContainerStyles} pointerEvents="box-none">
      <GestureHandlerRootView style={containerStyles} pointerEvents="box-none">
        <Animated.View style={deckStyles}>
          <Pressable
            ref={deckRef}
            style={styles.deckPressable}
            accessible={false}
            focusable={false}
            onPress={toggleExpanded}
            onPressIn={handlePressIn}
            onPressOut={handlePressOut}
          >
            {backToFrontIndexes.map((index) => (
              <NoticeToastStackItem
                key={visibleToasts[index].instanceId ?? visibleToasts[index].id}
                toast={visibleToasts[index]}
                placement={placement}
                deckOffset={isDeckExpanded ? expandedOffsets[index] : collapsedOffsets[index]}
                deckScale={isDeckExpanded ? 1 : collapsedScales[index]}
                isCovered={!isDeckExpanded && index > 0}
                isPaused={isPaused}
                isLiquidGlassEnabled={isLiquidGlassEnabled}
                onLayout={handleToastLayout}
                onDismiss={onDismiss}
                onInteractionStart={handleSwipeStart}
                onInteractionEnd={handleSwipeEnd}
              />
            ))}
          </Pressable>
        </Animated.View>
      </GestureHandlerRootView>
    </Animated.View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toBackToFrontIndex(_toast: Toast, index: number, toasts: readonly Toast[]) {
  return toasts.length - 1 - index;
}

function toCollapsedOffset(height: number, index: number, heights: number[]) {
  const [frontToastHeight = 0] = heights;
  const measuredHeight = height > 0 ? height : frontToastHeight;

  return frontToastHeight + index * TOAST_PEEK - measuredHeight;
}

function toExpandedOffset(_height: number, index: number, heights: number[]) {
  return sumHeights(heights.slice(0, index)) + index * TOAST_GAP;
}

function toCollapsedScale(_height: number, index: number) {
  return 1 - index * TOAST_SCALE_STEP;
}

function sumHeights(heights: number[]) {
  return heights.reduce(addHeight, 0);
}

function addHeight(total: number, height: number) {
  return total + height;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    paddingHorizontal: theme.spacing['4'],
  },
  offsetContainerBottom: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    left: 0,
  },
  deck: {
    width: '100%',
    maxWidth: 480,
  },
  deckPressable: {
    flex: 1,
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const MAX_VISIBLE_TOASTS = 3;
const TOAST_PEEK = 10;
const TOAST_GAP = theme.spacing['2'];
const TOAST_SCALE_STEP = 0.05;

const LAYOUT_TIMING = {
  duration: theme.transitions.base,
  easing: Easing.out(Easing.cubic),
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  toasts: readonly Toast[];
  placement: ComponentProps<typeof NoticeToastStackItem>['placement'];
  topGap: number;
  bottomGap?: number;
  isLiquidGlassEnabled?: boolean;
  onDismiss: ComponentProps<typeof NoticeToastStackItem>['onDismiss'];
};

type Toast = ComponentProps<typeof NoticeToastStackItem>['toast'];

type ToastHeightsById = Partial<Record<string, number>>;
